/**
 * BAKED GEAR PROJECTION IMPACT — REAL DATA, temporary transport.
 *
 * These are the nine `proj_*` point differentials already stored on every
 * `GearCatalog` row (and checked into `backend/templates/gear/gear.csv`).
 * They answer: "historically, how many fantasy points did this item add to a
 * player in this position/tier cohort?"
 *
 * ⚠️ THIS IS A TEMPORARY FRONTEND COPY, NOT A MOCK.
 * The numbers are genuine backend values, verified read-only against
 * `GearCatalog` in dev on 2026-09-27. They are duplicated here (rather than
 * fetched) purely to avoid adding gear-projection serialization that the real
 * projection API will supersede shortly. When that API lands, delete this file
 * and have `playerProjection.js` read the served values instead — no UI
 * component imports this module directly, so that swap is one file.
 *
 * SOURCE: GearCatalog, projection_source_year=2024, projection_source_week=null.
 *
 * KEYED BY `item_id` — the stable catalog identifier that `GearSerializer`
 * already exposes on every owned-gear payload. `name` is carried only as a
 * legacy fallback for rows whose `item_id` failed to resolve (the serializer
 * returns null in that broken-invariant case).
 *
 * ============================================================================
 * INCLUSION RULE — READ BEFORE EDITING. THIS IS LOAD-BEARING.
 * ============================================================================
 * Every `GearCatalog` row with a NON-NULL `projection_source_year` appears
 * here. As of 2026-09-27 that is 120 rows: 56 with at least one non-zero
 * differential, and **64 whose nine differentials are all 0.0**.
 *
 * ⚠️ THE 64 ALL-ZERO ROWS ARE INCLUDED ON PURPOSE. DO NOT "TIDY" THEM AWAY.
 *
 * A row carrying `0.0` **plus projection metadata** is an ANALYZED NEUTRAL
 * RESULT — the backend ran the analysis and found the item moves this cohort's
 * score by zero. That is a finding, not an absence. It is categorically
 * different from a row with `null` projections, which was never analyzed.
 *
 * An earlier revision of this file omitted the all-zero rows as "empty data".
 * That was a bug with real user-visible consequences, because omission makes
 * `getGearProjection()` return `null`:
 *
 *   • an equipped neutral item rendered as "—" (unknown) instead of "+0.00pts",
 *     telling the user we had no idea what their item does when in fact we know
 *     precisely that it does nothing; and
 *   • a loadout containing ONLY neutral items produced a `null` gear total via
 *     `sumGearProjection()` (whose `knownCount` stayed 0), so the whole Gear Adj
 *     section collapsed to "unavailable" for a perfectly well-understood
 *     loadout, and the item was counted in `unknownGearCount`.
 *
 * Both outcomes violate the feature's null-versus-zero contract: `0` means
 * "analyzed, no effect" and MUST render; `null` means "no data" and renders —.
 *
 * ONLY rows with `projection_source_year IS NULL` are excluded — genuinely
 * unanalyzed items plus the entire baseball catalog (item_ids 2xxx/3xxx), which
 * has no football projections by design. For those, an absent lookup correctly
 * means "no projection data".
 */

/**
 * ANALYZED-NEUTRAL ITEMS — all nine differentials are exactly 0.0.
 *
 * Listed as `item_id: 'name'` pairs and expanded into full entries below,
 * because writing out 64 identical nine-zero blocks would bury the 56 rows
 * that actually carry signal. Each still becomes a first-class entry in
 * `GEAR_PROJECTION_IMPACT`, indistinguishable at lookup time from a row
 * written longhand — `getGearProjection()` returns `0`, never `null`.
 *
 * Several of these are unimplemented or joke items whose scoring hooks do
 * nothing yet; others are implemented but genuinely didn't move the needle for
 * any cohort. Both are legitimate zero findings and are surfaced as +0.00pts.
 */
const ANALYZED_NEUTRAL_ITEMS = {
    1001: 'Friendship Bracelet',
    1005: 'Big #1 Foam Finger',
    1008: 'Astronaut Boots',
    1009: 'Football Yard Stick',
    1010: 'Deja Vu gloves',
    1011: 'Gloves of Overachievement',
    1012: 'Boots of Improvement',
    1013: 'Rose Tinted Glasses',
    1014: 'Short Shorts',
    1015: 'Clutch Jeans',
    1024: 'High Floor',
    1025: 'Beast Mode',
    1026: 'Moss Mode',
    1027: 'Peyton Mode',
    1028: 'Ticker Tape',
    1029: 'Chaos Tattoo',
    1032: 'Scrambled Egg Salad Sandwich',
    1033: 'Potion of Speed',
    1036: 'Potion of Pushups',
    1037: 'Potion of Sneaky',
    1042: 'Potion of Focus',
    1047: 'Cat Claws',
    1048: 'Old Timey Leather Helmet',
    1049: 'Boots of Buttkicking',
    1051: 'Ultimate Safety Helmet',
    1053: 'Oldie But Goodie Gloves',
    1054: 'Old Smelly Shoes',
    1055: 'First Mate Hat',
    1056: 'Glovey Shoes',
    1057: 'Team Uniform',
    1058: 'Odd Ball',
    1059: 'You Play to Win Mouthguard',
    1061: 'Hate Blockers',
    1062: '2nd half hero cape',
    1063: 'Sore Loser Bandana',
    1064: '24 Karat Gold Chain',
    1065: 'Wrong Pads',
    1066: 'Glass Eye on the Ball',
    1067: 'Inflatable Arms',
    1069: 'Snappy Pair of Boots',
    1070: 'Colorful Bandaids',
    1071: 'Shoey Gloves',
    1072: 'Salty Time Piece',
    1073: 'Foot Ball',
    1074: 'Rock Tote bag',
    1075: 'Fall on Face Mask',
    1076: 'No Pain No Gain pants',
    1077: 'Okaaaaay Clock Chain',
    1078: 'Wide Open Goggles',
    1079: 'Short King',
    1080: 'Useless Finger Gloves',
    1081: 'Poets Beret',
    1082: 'Contract Bonus',
    1083: 'Home Body',
    1084: 'Super Snappy Gloves',
    1085: 'Tear Away Pants',
    1086: 'Less TD pants',
    1087: "[rare] Captain's Badge",
    1088: "Poet's Striped Shirt",
    1096: 'NEW NAME',
    1097: 'NEW NAME 2',
    1098: 'NEW NAME 3',
    1122: 'Merry Go Round',
    1124: 'New Gear Item 1124',
};

/** Expand one neutral item id/name into the standard nine-differential entry. */
function neutralEntry(name) {
    return {
        name,
        QB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
    };
}

/**
 * Point differentials by item_id → position → tier.
 * Tier keys are TOP / MID / LOW, matching `HISTORICAL_PROJECTION_2025`.
 * Values are raw backend floats; round only at render time.
 *
 * Contains all 120 analyzed catalog rows — the 56 with signal are written
 * longhand below, and the 64 analyzed-neutral rows are merged in afterwards.
 */
export const GEAR_PROJECTION_IMPACT = {
    1000: {
        name: 'Spirit of the game',
        QB: { TOP: 9.51, MID: 7.91, LOW: 5.99 },
        RB: { TOP: 6.36, MID: 5.13, LOW: 2.42 },
        WR: { TOP: 5.44, MID: 4.38, LOW: 3.20 },
    },
    1002: {
        name: 'Cleats of Teleportation',
        QB: { TOP: 6.48, MID: 3.26, LOW: 3.46 },
        RB: { TOP: 15.50, MID: 12.51, LOW: 5.48 },
        WR: { TOP: 0.21, MID: 0.25, LOW: 0.32 },
    },
    1003: {
        name: 'Legendary Ball Hog Gloves',
        QB: { TOP: 0.01, MID: 0.01, LOW: 0.0 },
        RB: { TOP: 2.83, MID: 2.65, LOW: 1.62 },
        WR: { TOP: 5.81, MID: 4.88, LOW: 3.42 },
    },
    1004: {
        name: 'Perfectly Deflated Football',
        QB: { TOP: 18.94, MID: 19.61, LOW: 15.87 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 0.01, MID: 0.0, LOW: 0.01 },
    },
    1006: {
        name: '"Get Shifty" Pre-game Music',
        QB: { TOP: 0.01, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 4.54, MID: 4.09, LOW: 2.34 },
        WR: { TOP: 14.97, MID: 12.29, LOW: 8.72 },
    },
    1007: {
        name: 'Scoring Playbook Audible',
        QB: { TOP: 30.14, MID: 33.38, LOW: 27.64 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 0.01, MID: 0.0, LOW: 0.01 },
    },
    1016: {
        name: 'Antigravity Jockstrap',
        QB: { TOP: 5.24, MID: 5.35, LOW: 4.92 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 0.0, MID: 0.01, LOW: 0.01 },
    },
    1017: {
        name: 'Frying Pan Hands',
        QB: { TOP: 0.0, MID: 0.0, LOW: 0.02 },
        RB: { TOP: 1.28, MID: 1.26, LOW: 0.84 },
        WR: { TOP: 5.52, MID: 5.20, LOW: 4.28 },
    },
    1018: {
        name: 'Guardian Helmet',
        QB: { TOP: 1.79, MID: 1.06, LOW: 1.05 },
        RB: { TOP: 4.99, MID: 4.33, LOW: 1.99 },
        WR: { TOP: 0.07, MID: 0.09, LOW: 0.07 },
    },
    1019: {
        name: 'Gold Fishing Rod',
        QB: { TOP: 0.01, MID: 0.0, LOW: 0.01 },
        RB: { TOP: 1.39, MID: 1.31, LOW: 0.81 },
        WR: { TOP: 3.42, MID: 2.99, LOW: 2.23 },
    },
    1020: {
        name: 'Passing Grade Report Card',
        QB: { TOP: 7.64, MID: 8.25, LOW: 7.06 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
    },
    1021: {
        name: 'Cool Runnings',
        QB: { TOP: 2.43, MID: 1.22, LOW: 1.30 },
        RB: { TOP: 5.81, MID: 4.69, LOW: 2.05 },
        WR: { TOP: 0.08, MID: 0.09, LOW: 0.12 },
    },
    1022: {
        name: 'Passed Life',
        QB: { TOP: 7.11, MID: 7.36, LOW: 5.95 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
    },
    1023: {
        name: 'Catch the Wind',
        QB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 1.70, MID: 1.53, LOW: 0.88 },
        WR: { TOP: 5.61, MID: 4.61, LOW: 3.27 },
    },
    1030: {
        name: 'Double Fisted Gloves',
        QB: { TOP: 1.20, MID: 0.71, LOW: 0.71 },
        RB: { TOP: 4.02, MID: 3.54, LOW: 1.74 },
        WR: { TOP: 1.76, MID: 1.56, LOW: 1.16 },
    },
    1031: {
        name: 'Mismatched Shoes',
        QB: { TOP: 7.31, MID: 7.31, LOW: 6.35 },
        RB: { TOP: 3.33, MID: 2.89, LOW: 1.33 },
        WR: { TOP: 0.05, MID: 0.06, LOW: 0.05 },
    },
    1034: {
        name: 'Six-fingered Glove',
        QB: { TOP: 0.01, MID: 0.01, LOW: 0.0 },
        RB: { TOP: 1.70, MID: 1.59, LOW: 0.97 },
        WR: { TOP: 3.48, MID: 2.93, LOW: 2.05 },
    },
    1035: {
        name: 'Toss Far Potion',
        QB: { TOP: 1.60, MID: 1.60, LOW: 1.60 },
        RB: { TOP: 1.60, MID: 1.60, LOW: 1.60 },
        WR: { TOP: 1.60, MID: 1.60, LOW: 1.60 },
    },
    1038: {
        name: 'Potion of Accuracy',
        QB: { TOP: 4.02, MID: 4.46, LOW: 3.69 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
    },
    1039: {
        name: 'Bag of Holding',
        QB: { TOP: 1.10, MID: 0.85, LOW: 0.85 },
        RB: { TOP: 2.16, MID: 1.94, LOW: 1.16 },
        WR: { TOP: 0.52, MID: 0.53, LOW: 0.53 },
    },
    1040: {
        name: 'Potion of Visualization',
        QB: { TOP: 0.50, MID: 0.50, LOW: 0.50 },
        RB: { TOP: 0.85, MID: 0.83, LOW: 0.70 },
        WR: { TOP: 1.35, MID: 1.25, LOW: 1.06 },
    },
    1041: {
        name: 'Potion of Omaha',
        QB: { TOP: 3.56, MID: 3.80, LOW: 3.33 },
        RB: { TOP: 0.50, MID: 0.50, LOW: 0.50 },
        WR: { TOP: 0.50, MID: 0.50, LOW: 0.50 },
    },
    1043: {
        name: 'Potion of Bad Defense',
        QB: { TOP: 2.51, MID: 2.73, LOW: 2.34 },
        RB: { TOP: 0.50, MID: 0.50, LOW: 0.50 },
        WR: { TOP: 0.50, MID: 0.50, LOW: 0.50 },
    },
    1044: {
        name: 'Rocket Rollerskates',
        QB: { TOP: 1.62, MID: 0.81, LOW: 0.86 },
        RB: { TOP: 5.01, MID: 4.15, LOW: 1.95 },
        WR: { TOP: 3.79, MID: 3.14, LOW: 2.26 },
    },
    1045: {
        name: 'Runningback Jetpack',
        QB: { TOP: 2.0, MID: 1.0, LOW: 1.0 },
        RB: { TOP: 5.0, MID: 4.0, LOW: 1.0 },
        WR: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
    },
    1046: {
        name: 'VR headset',
        QB: { TOP: 4.0, MID: 4.0, LOW: 4.0 },
        RB: { TOP: 4.0, MID: 4.0, LOW: 4.0 },
        WR: { TOP: 4.0, MID: 4.0, LOW: 4.0 },
    },
    1050: {
        name: 'Sleight of Hand Sleeves',
        QB: { TOP: 4.88, MID: 5.20, LOW: 5.04 },
        RB: { TOP: 1.12, MID: 1.28, LOW: 0.72 },
        WR: { TOP: 0.48, MID: 0.32, LOW: 0.24 },
    },
    1052: {
        name: 'Curling Cap',
        QB: { TOP: 6.36, MID: 5.72, LOW: 4.83 },
        RB: { TOP: 3.87, MID: 3.13, LOW: 1.37 },
        WR: { TOP: 0.05, MID: 0.06, LOW: 0.08 },
    },
    1060: {
        name: 'Terrible Two-step Shoes',
        QB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 1.0, MID: 1.0, LOW: 0.0 },
        WR: { TOP: 2.0, MID: 2.0, LOW: 1.0 },
    },
    1068: {
        name: 'Eggs Benedict on my face',
        QB: { TOP: 1.68, MID: 2.72, LOW: 2.56 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
    },
    1089: {
        name: 'Terrific Catch Potion',
        QB: { TOP: 0.01, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 0.85, MID: 0.79, LOW: 0.48 },
        WR: { TOP: 1.74, MID: 1.46, LOW: 1.03 },
    },
    1090: {
        name: 'Potion of Strength',
        QB: { TOP: 1.20, MID: 0.71, LOW: 0.70 },
        RB: { TOP: 3.32, MID: 2.89, LOW: 1.33 },
        WR: { TOP: 0.05, MID: 0.06, LOW: 0.05 },
    },
    1091: {
        name: 'Make-A-Wish Gloves',
        QB: { TOP: 0.01, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 0.69, MID: 0.65, LOW: 0.40 },
        WR: { TOP: 1.71, MID: 1.50, LOW: 1.11 },
    },
    1100: {
        name: 'Minitron Mode',
        QB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 4.0, MID: 4.0, LOW: 0.0 },
    },
    1101: {
        name: 'Eli Mode',
        QB: { TOP: 4.0, MID: 4.0, LOW: 4.0 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
    },
    1102: {
        name: 'Flacco Cannon',
        QB: { TOP: 4.0, MID: 4.0, LOW: 3.0 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
    },
    1103: {
        name: 'Ruler of Threes',
        QB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 4.0, MID: 4.0, LOW: 4.0 },
    },
    1104: {
        name: 'Zac Attack',
        QB: { TOP: 0.01, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 2.27, MID: 2.05, LOW: 1.17 },
        WR: { TOP: 7.49, MID: 6.15, LOW: 4.36 },
    },
    1105: {
        name: 'Third Arm',
        QB: { TOP: 0.01, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 2.27, MID: 2.05, LOW: 1.17 },
        WR: { TOP: 7.49, MID: 6.15, LOW: 4.36 },
    },
    1106: {
        name: 'Third Leg',
        QB: { TOP: 3.24, MID: 1.63, LOW: 1.73 },
        RB: { TOP: 7.75, MID: 6.25, LOW: 2.74 },
        WR: { TOP: 0.10, MID: 0.12, LOW: 0.16 },
    },
    1107: {
        name: 'Whirlylegs',
        QB: { TOP: 0.81, MID: 0.41, LOW: 0.43 },
        RB: { TOP: 2.50, MID: 2.07, LOW: 0.98 },
        WR: { TOP: 1.90, MID: 1.57, LOW: 1.13 },
    },
    1108: {
        name: 'Pennywhistle',
        QB: { TOP: 3.18, MID: 2.86, LOW: 2.42 },
        RB: { TOP: 1.94, MID: 1.56, LOW: 0.68 },
        WR: { TOP: 0.02, MID: 0.03, LOW: 0.04 },
    },
    1109: {
        name: 'Target Practice',
        QB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 0.16, MID: 0.16, LOW: 0.10 },
        WR: { TOP: 0.69, MID: 0.65, LOW: 0.54 },
    },
    1110: {
        name: 'Walkabout',
        QB: { TOP: 0.60, MID: 0.35, LOW: 0.35 },
        RB: { TOP: 1.66, MID: 1.44, LOW: 0.66 },
        WR: { TOP: 0.02, MID: 0.03, LOW: 0.03 },
    },
    1111: {
        name: 'Triple Threat Gloves',
        QB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 0.52, MID: 0.49, LOW: 0.30 },
        WR: { TOP: 1.28, MID: 1.12, LOW: 0.84 },
    },
    1112: {
        name: 'Fudge Nudge',
        QB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 1.0, MID: 1.0, LOW: 0.0 },
        WR: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
    },
    1113: {
        name: 'Heelies',
        QB: { TOP: 1.30, MID: 0.65, LOW: 0.69 },
        RB: { TOP: 3.10, MID: 2.50, LOW: 1.09 },
        WR: { TOP: 0.04, MID: 0.05, LOW: 0.07 },
    },
    1114: {
        name: 'Flag Drawing Board',
        QB: { TOP: 3.79, MID: 3.92, LOW: 3.18 },
        RB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        WR: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
    },
    1115: {
        name: "Catcher's Mitt",
        QB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 0.91, MID: 0.82, LOW: 0.46 },
        WR: { TOP: 2.99, MID: 2.46, LOW: 1.74 },
    },
    1116: {
        name: 'Drop in the Bucket',
        QB: { TOP: 1.50, MID: 1.50, LOW: 1.50 },
        RB: { TOP: 1.50, MID: 1.50, LOW: 1.50 },
        WR: { TOP: 1.50, MID: 1.50, LOW: 1.50 },
    },
    1117: {
        name: 'Evening Stroll',
        QB: { TOP: 1.50, MID: 1.50, LOW: 1.50 },
        RB: { TOP: 1.50, MID: 1.50, LOW: 1.50 },
        WR: { TOP: 1.50, MID: 1.50, LOW: 1.50 },
    },
    1118: {
        name: "Jerry's Rice",
        QB: { TOP: 2.0, MID: 2.0, LOW: 2.0 },
        RB: { TOP: 2.0, MID: 2.0, LOW: 2.0 },
        WR: { TOP: 2.0, MID: 2.0, LOW: 2.0 },
    },
    1119: {
        name: 'Brick House Build',
        QB: { TOP: 2.0, MID: 2.0, LOW: 2.0 },
        RB: { TOP: 2.0, MID: 2.0, LOW: 2.0 },
        WR: { TOP: 2.0, MID: 2.0, LOW: 2.0 },
    },
    1120: {
        name: 'Throw the Distance',
        QB: { TOP: 4.80, MID: 4.80, LOW: 4.80 },
        RB: { TOP: 4.80, MID: 4.80, LOW: 4.80 },
        WR: { TOP: 4.80, MID: 4.80, LOW: 4.80 },
    },
    1121: {
        name: 'Mini Beast Mode',
        QB: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
        RB: { TOP: 4.0, MID: 4.0, LOW: 0.0 },
        WR: { TOP: 0.0, MID: 0.0, LOW: 0.0 },
    },
    1123: {
        name: 'Very Intentional Grounding',
        QB: { TOP: 20.47, MID: 20.70, LOW: 19.83 },
        RB: { TOP: 10.01, MID: 10.0, LOW: 10.0 },
        WR: { TOP: 10.0, MID: 10.01, LOW: 10.01 },
    },
};

// Merge the 64 analyzed-neutral rows in. They are every bit as real as the
// entries above — see the INCLUSION RULE in this file's header for why dropping
// them silently converts "+0.00pts" into "—" and breaks loadouts made entirely
// of neutral items.
Object.entries(ANALYZED_NEUTRAL_ITEMS).forEach(([itemId, name]) => {
    if (GEAR_PROJECTION_IMPACT[itemId] !== undefined) {
        // A neutral row must never silently overwrite a row with signal.
        throw new Error(
            `gearProjectionImpact: item_id ${itemId} ("${name}") is listed in ` +
            'ANALYZED_NEUTRAL_ITEMS but already has a longhand entry with ' +
            'projection values. One of the two is wrong — resolve against ' +
            'GearCatalog rather than letting a zero clobber real data.'
        );
    }
    GEAR_PROJECTION_IMPACT[itemId] = neutralEntry(name);
});

/**
 * Provenance of the values above, surfaced in the UI so a user is never told a
 * 2024-derived estimate is a live weekly projection.
 */
export const GEAR_PROJECTION_SOURCE = {
    year: 2024,
    week: 0,
    method: 'baked-historical-differential',
};

/**
 * Name-keyed fallback index.
 *
 * `GearSerializer.get_item_id()` returns null when an owned `Gear` row
 * references a missing `GearCatalog` row — a logged, broken production
 * invariant. Rather than drop the projection entirely in that case, we can
 * still resolve by name, which is the same key scoring itself uses.
 */
const IMPACT_BY_NAME = Object.entries(GEAR_PROJECTION_IMPACT).reduce(
    (index, [itemId, entry]) => {
        index[entry.name.toLowerCase()] = { ...entry, itemId: Number(itemId) };
        return index;
    },
    {}
);

/**
 * Resolve one gear item's projected point differential for a position/tier.
 *
 * @param {Object} gear - an owned-gear payload (`item_id` preferred, `name` fallback)
 * @param {'QB'|'RB'|'WR'} position
 * @param {'TOP'|'MID'|'LOW'} tier
 * @returns {number|null} the point differential, or null when unknown.
 *
 * NULL vs 0 IS A REAL DISTINCTION AND MUST BE PRESERVED IN BOTH DIRECTIONS.
 *
 *   • `0`    — the backend analyzed this item and found it adds nothing for
 *              this cohort. A legitimate finding; renders as "+0.00pts".
 *   • `null` — we have no data at all; renders as unavailable ("—").
 *
 * Collapsing null into 0 would invent a finding the backend never made.
 * Collapsing 0 into null is equally wrong and is the subtler mistake: it throws
 * away a real answer and tells the user we are ignorant of an item we in fact
 * understand completely. Both failures are silent to a reader of the rendered
 * UI, which is why the fixture above deliberately carries all 64 analyzed-
 * neutral rows rather than letting them fall through to the null path here.
 *
 * `null` is returned ONLY when: the item has no catalog entry (unanalyzed or
 * baseball), the position/tier is unknown, or the stored value is not numeric.
 */
export function getGearProjection(gear, position, tier) {
    if (!gear) {
        throw new Error(
            'getGearProjection() called without a gear item. Expected an owned-gear ' +
            'payload carrying at least item_id or name.'
        );
    }

    if (!position || !tier) return null;

    const byId = gear.item_id != null ? GEAR_PROJECTION_IMPACT[gear.item_id] : null;
    const entry = byId || (gear.name ? IMPACT_BY_NAME[gear.name.toLowerCase()] : null);

    if (!entry) return null;

    const byPosition = entry[position];
    if (!byPosition) return null;

    const value = byPosition[tier];
    return typeof value === 'number' ? value : null;
}
